import React, { createContext, useContext, useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, updateProfile, GoogleAuthProvider, signInWithPopup, signInWithRedirect } from 'firebase/auth';
import api from '../services/api';
import { FIREBASE_CONFIG, IS_PRODUCTION, API_URL } from '../utils/constants';
import { authService } from '../services/authService';

// Initialize Firebase with error handling
let app = null;
let auth = null;

try {
  // Check if Firebase config is valid
  if (FIREBASE_CONFIG.apiKey && FIREBASE_CONFIG.projectId) {
    app = initializeApp(FIREBASE_CONFIG);
    auth = getAuth(app);
  } else {
    console.warn('Firebase configuration is missing. Please set up Firebase environment variables.');
  }
} catch (error) {
  console.error('Error initializing Firebase:', error);
}

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

const isMobileAuth = () =>
  typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);

  useEffect(() => {
    if (!auth) {
      setLoading(false);
      return;
    }
    
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Get Firebase ID token
          const idToken = await firebaseUser.getIdToken();
          localStorage.setItem('firebase_id_token', idToken);

          // Verify token with backend and get user profile
          const response = await authService.verifyToken(idToken);
          setUser(firebaseUser);
          setUserProfile(response.user);
        } catch (error) {
          console.error('Error verifying token:', error);
          setUser(null);
          setUserProfile(null);
          localStorage.removeItem('firebase_id_token');
        }
      } else {
        setUser(null);
        setUserProfile(null);
        localStorage.removeItem('firebase_id_token');
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signUp = async (email, password, displayName = null) => {
    if (!auth) {
      return { success: false, error: 'Firebase is not configured. Please set up Firebase environment variables.' };
    }
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update Firebase user profile with display name if provided
      if (displayName && userCredential.user) {
        try {
          await updateProfile(userCredential.user, {
            displayName: displayName
          });
        } catch (updateError) {
          console.warn('Failed to update Firebase display name:', updateError);
          // Continue with signup even if display name update fails
        }
      }
      
      const idToken = await userCredential.user.getIdToken();
      localStorage.setItem('firebase_id_token', idToken);

      // Verify token with backend
      try {
        const response = await authService.verifyToken(idToken);
        setUserProfile(response.user);
        return { success: true, user: userCredential.user };
      } catch (backendError) {
        // Handle backend API errors
        console.error('Backend verification error:', backendError);
        console.error('Backend error details:', {
          message: backendError.message,
          code: backendError.code,
          response: backendError.response,
          request: backendError.request,
          config: backendError.config
        });
        
        // Check if it's a network error
        // Only treat as network error if there's no response AND it's a request error
        const isNetworkError = (
          backendError.message?.includes('Network Error') || 
          backendError.code === 'ERR_NETWORK' ||
          backendError.message?.includes('Failed to fetch') ||
          (!backendError.response && backendError.request)
        );
        
        if (isNetworkError) {
          // Provide different error messages for production vs development
          const errorMessage = IS_PRODUCTION
            ? `Cannot connect to server. Please check that the backend API is running and accessible at ${API_URL}. If you're the site administrator, verify that the VITE_API_URL environment variable was set correctly during the build process.`
            : 'Cannot connect to server. Please make sure the backend is running on http://localhost:8000';
          
          return { 
            success: false, 
            error: errorMessage,
            errorCode: 'NETWORK_ERROR'
          };
        }
        
        // Handle other backend errors
        const errorMessage = backendError.response?.data?.detail || 
                            backendError.response?.data?.message || 
                            backendError.message || 
                            'Failed to verify with server. Please try again.';
        
        return { 
          success: false, 
          error: errorMessage,
          errorCode: backendError.response?.status || 'BACKEND_ERROR'
        };
      }
    } catch (error) {
      // Handle Firebase authentication errors
      let errorMessage = error.message;
      
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'This email is already registered. Please sign in instead.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Please enter a valid email address.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password should be at least 6 characters.';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      return { success: false, error: errorMessage, errorCode: error.code };
    }
  };

  const signIn = async (email, password) => {
    if (!auth) {
      return { success: false, error: 'Firebase is not configured. Please set up Firebase environment variables.' };
    }
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const idToken = await userCredential.user.getIdToken();
      localStorage.setItem('firebase_id_token', idToken);

      // Verify token with backend
      try {
        const response = await authService.verifyToken(idToken);
        setUserProfile(response.user);
        return { success: true, user: userCredential.user };
      } catch (backendError) {
        // Handle backend API errors
        console.error('Backend verification error:', backendError);
        console.error('Backend error details:', {
          message: backendError.message,
          code: backendError.code,
          response: backendError.response,
          request: backendError.request,
          config: backendError.config
        });
        
        // Check if it's a network error or timeout
        // Only treat as network error if there's no response AND it's a request error
        const isNetworkError = (
          backendError.message?.includes('Network Error') || 
          backendError.code === 'ERR_NETWORK' ||
          backendError.message?.includes('Failed to fetch') ||
          backendError.code === 'ECONNREFUSED' ||
          backendError.code === 'ETIMEDOUT' ||
          (backendError.code === 'ECONNABORTED' && backendError.message?.includes('timeout')) ||
          (!backendError.response && backendError.request)
        );
        
        if (isNetworkError) {
          // Provide different error messages for production vs development
          const errorMessage = IS_PRODUCTION
            ? `Cannot connect to server. Please check that the backend API is running and accessible at ${API_URL}. If you're the site administrator, verify that the VITE_API_URL environment variable was set correctly during the build process.`
            : 'Cannot connect to server. Please make sure the backend is running on http://localhost:8000. Start it with: cd backend && source venv/bin/activate && uvicorn app.main:app --reload';
          
          return { 
            success: false, 
            error: errorMessage,
            errorCode: 'NETWORK_ERROR'
          };
        }
        
        // Handle specific HTTP status codes
        if (backendError.response?.status === 503) {
          return { 
            success: false, 
            error: 'Database is not available. Please ensure MongoDB is running.',
            errorCode: 'DATABASE_ERROR'
          };
        }
        
        // Handle other backend errors
        const errorMessage = backendError.response?.data?.detail || 
                            backendError.response?.data?.message || 
                            backendError.message || 
                            'Failed to verify with server. Please try again.';
        
        return { 
          success: false, 
          error: errorMessage,
          errorCode: backendError.response?.status || 'BACKEND_ERROR'
        };
      }
    } catch (error) {
      // Handle Firebase authentication errors
      let errorMessage = error.message;
      
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email. Please sign up first.';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password. Please try again.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Please enter a valid email address.';
      } else if (error.code === 'auth/invalid-credential') {
        errorMessage = 'Invalid email or password. Please try again.';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      return { success: false, error: errorMessage, errorCode: error.code };
    }
  };

  const signInWithGoogle = async () => {
    if (!auth) {
      return { success: false, error: 'Firebase is not configured. Please set up Firebase environment variables.' };
    }

    try {
      if (isMobileAuth()) {
        await signInWithRedirect(auth, googleProvider);
        return { success: true, redirecting: true };
      }

      const userCredential = await signInWithPopup(auth, googleProvider);
      const idToken = await userCredential.user.getIdToken();
      localStorage.setItem('firebase_id_token', idToken);

      try {
        const response = await authService.verifyToken(idToken);
        setUserProfile(response.user);
        return { success: true, user: userCredential.user };
      } catch (backendError) {
        const isNetworkError = (
          backendError.message?.includes('Network Error') ||
          backendError.code === 'ERR_NETWORK' ||
          backendError.message?.includes('Failed to fetch') ||
          (!backendError.response && backendError.request)
        );

        if (isNetworkError) {
          const errorMessage = IS_PRODUCTION
            ? `Cannot connect to server. Please check that the backend API is running and accessible at ${API_URL}.`
            : 'Cannot connect to server. Please make sure the backend is running on http://localhost:8000';
          return { success: false, error: errorMessage, errorCode: 'NETWORK_ERROR' };
        }

        const errorMessage = backendError.response?.data?.detail ||
          backendError.response?.data?.message ||
          backendError.message ||
          'Failed to verify with server. Please try again.';

        return {
          success: false,
          error: errorMessage,
          errorCode: backendError.response?.status || 'BACKEND_ERROR'
        };
      }
    } catch (error) {
      let errorMessage = 'Google sign-in failed. Please try again.';

      if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
        return { success: false, error: 'Google sign-in was cancelled.', errorCode: error.code };
      }
      if (error.code === 'auth/popup-blocked') {
        errorMessage = 'The sign-in popup was blocked. Please allow popups and try again.';
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        errorMessage = 'An account already exists with this email. Sign in with your email and password instead.';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      return { success: false, error: errorMessage, errorCode: error.code };
    }
  };

  const logout = async () => {
    if (!auth) {
      return;
    }
    try {
      await signOut(auth);
      setUser(null);
      setUserProfile(null);
      localStorage.removeItem('firebase_id_token');
      localStorage.removeItem('user');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const refreshUserProfile = async () => {
    if (user) {
      try {
        const profile = await authService.getCurrentUser();
        setUserProfile(profile);
      } catch (error) {
        console.error('Error refreshing user profile:', error);
      }
    }
  };

  const sendPasswordReset = async (email) => {
    try {
      console.log('[DEBUG] Sending password reset request:', {
        email: email,
        endpoint: '/auth/password-reset',
        apiBaseURL: api.defaults.baseURL
      });
      
      // Use our backend API to send custom Brevo email template
      const response = await api.post('/auth/password-reset', {
        email: email,
        continue_url: `${window.location.origin}/signin`
      });
      
      console.log('[DEBUG] Password reset response:', response.data);
      
      if (response.data.success) {
        return { 
          success: true, 
          message: response.data.message || 'Password reset email sent! Please check your inbox (and spam folder). The link will expire in 1 hour.' 
        };
      } else {
        return { 
          success: false, 
          error: response.data.message || 'Failed to send password reset email. Please try again.' 
        };
      }
    } catch (error) {
      // Provide user-friendly error messages
      let errorMessage = 'Failed to send password reset email. Please try again.';
      
      console.error('[ERROR] Password reset error details:', {
        error: error,
        hasResponse: !!error.response,
        hasRequest: !!error.request,
        responseStatus: error.response?.status,
        responseData: error.response?.data,
        requestURL: error.config?.url,
        requestBaseURL: error.config?.baseURL,
        fullURL: error.config?.baseURL + error.config?.url,
        message: error.message,
        code: error.code
      });
      
      if (error.response) {
        // Backend returned an error
        errorMessage = error.response.data?.detail || error.response.data?.message || errorMessage;
      } else if (error.request) {
        // Request was made but no response received
        const apiUrl = error.config?.baseURL || 'http://localhost:8000/api';
        console.error('[ERROR] Network error - no response received. Possible causes:');
        console.error('  1. Backend server is not running');
        console.error('     → Check if backend is running: curl http://localhost:8000/api/health');
        console.error('     → Start backend: cd backend && source venv/bin/activate && uvicorn app.main:app --reload');
        console.error('  2. CORS issue - check backend CORS configuration');
        console.error('  3. Wrong API URL:', apiUrl);
        console.error('  4. Network connectivity issue');
        console.error('  5. Backend endpoint not accessible');
        errorMessage = `Network error. The backend server at ${apiUrl.replace('/api', '')} is not responding. Please ensure the backend server is running.`;
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    logout,
    refreshUserProfile,
    sendPasswordReset,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

